<!-- Console action feedback 0.1.155; 2026-10-28 18:11:13 CST; parent 44dbda7a05c960e3c79c6c322d676ac5e4982b20. -->
<!-- 当前运营控制台源码维护说明，更新时间：2026-09-01 01:01 CST。后续修改本文件功能时，请同步更新注释、版本号和更新时间。 -->
<script setup lang="ts">
import { onMounted, ref } from 'vue';
import EmptyState from '@hotx-cdn/shared/components/EmptyState.vue';
import { consoleApi, hasConsoleSession } from '../api/client';
import { displayDateTime } from '../utils/formatters';
import type {
  AccessSummary,
  CountryStat,
  Domain,
  LogPage,
  OperationLog,
  PublishLog,
  SecurityLog,
  StatusCodeStat,
  TopIpStat
} from '../api/types';

/*
 * Logs boundary:
 * Access, operation, publish, and security logs are read-only customer evidence.
 * Missing logs should be investigated in the OpenResty -> Agent -> backend ingest chain, not hidden by frontend fallback data.
 * Keep customer isolation strict for every log query.
 */
const domains = ref<Domain[]>([]);
const selectedDomainId = ref<number>();
const access = ref<AccessSummary | null>(null);
const operations = ref<LogPage<OperationLog> | null>(null);
const publishLogs = ref<LogPage<PublishLog> | null>(null);
const securityLogs = ref<SecurityLog[]>([]);
const topIps = ref<TopIpStat[]>([]);
const statusCodes = ref<StatusCodeStat[]>([]);
const countries = ref<CountryStat[]>([]);
const error = ref('');
const loading = ref(false);
const feedback = ref('');
const COUNTRY_NAMES: Record<string, string> = {
  CN: '中国大陆',
  HK: '中国香港',
  MO: '中国澳门',
  TW: '中国台湾',
  SG: '新加坡',
  MY: '马来西亚',
  VN: '越南',
  TH: '泰国',
  ID: '印度尼西亚',
  JP: '日本',
  KR: '韩国',
  US: '美国',
  CA: '加拿大',
  GB: '英国',
  DE: '德国',
  FR: '法国',
  AU: '澳大利亚'
};

onMounted(async () => {
  if (!hasConsoleSession()) return;
  try {
    domains.value = await consoleApi.domains.list();
    selectedDomainId.value = domains.value[0]?.id;
    await load();
  } catch (e) { error.value = e instanceof Error ? e.message : '日志域名加载失败，请重试。'; }
});

async function load(showFeedback = false) {
  if (loading.value) return;
  if (!hasConsoleSession()) { error.value = '登录后查询日志。'; return; }
  loading.value = true; feedback.value = '';
  try {
    error.value = '';
    const params = { domainId: selectedDomainId.value, page: 1, pageSize: 20 };
    const topParams = { domainId: selectedDomainId.value, limit: 10 };
    const [accessSummary, operationPage, securityRows, publishPage, ipStats, statusStats, countryStats] =
      await Promise.all([
        consoleApi.logs.access(params),
        consoleApi.logs.operations({ page: 1, pageSize: 20 }),
        consoleApi.logs.security(topParams),
        consoleApi.logs.publish(params),
        consoleApi.logs.topIps(topParams),
        consoleApi.logs.statusCodes({ domainId: selectedDomainId.value }),
        consoleApi.logs.countries(topParams)
      ]);
    access.value = accessSummary;
    operations.value = operationPage;
    securityLogs.value = securityRows;
    publishLogs.value = publishPage;
    topIps.value = ipStats;
    statusCodes.value = statusStats;
    countries.value = countryStats;
    if (showFeedback) feedback.value = '日志查询完成，数据已更新。';
  } catch (e) {
    error.value = e instanceof Error ? e.message : '日志加载失败';
  } finally { loading.value = false; }
}

function metric(primary: keyof AccessSummary, fallback: keyof AccessSummary) {
  return access.value?.[primary] ?? access.value?.[fallback] ?? 0;
}

function formatBytes(value: unknown) {
  if (typeof value !== 'number') return '0 B';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / 1024 / 1024).toFixed(2)} MB`;
}

function formatPercent(value: unknown) {
  return typeof value === 'number' ? `${value}%` : '0%';
}

function formatNumber(value: unknown) {
  return typeof value === 'number' ? value.toLocaleString('zh-CN') : '0';
}

function shareLabel(value: unknown) {
  return typeof value === 'number' ? `${value}%` : '占比暂无';
}

function releaseLabel(row: PublishLog) {
  return typeof row.releaseNo === 'number' ? `Release ${row.releaseNo}` : `发布记录 ${row.id}`;
}

function nodeProgress(row: PublishLog) {
  const success = row.successNodeCount ?? 0;
  const target = row.targetNodeCount ?? 0;
  return `节点 ${success}/${target}`;
}

function countryLabel(code: string) {
  return COUNTRY_NAMES[code] ? `${COUNTRY_NAMES[code]}（${code}）` : code;
}

function logTimeText(row: { completedAt?: string; createdAt?: string }) {
  return displayDateTime(row.completedAt || row.createdAt);
}
</script>

<template>
  <section class="page">
    <div class="page-title">
      <h1>日志查询</h1>
      <p>仅展示当前客户的聚合访问、安全、发布和操作日志。</p>
    </div>
    <el-alert v-if="error" :title="error" type="error" show-icon />
    <p v-if="feedback" role="status">{{ feedback }}</p>
    <div class="inline-control">
      <el-select v-model="selectedDomainId" placeholder="全部域名">
        <el-option v-for="domain in domains" :key="domain.id" :label="domain.domain" :value="domain.id" />
      </el-select>
      <el-button :loading="loading" :disabled="loading" @click="load(true)">{{ loading ? '查询中…' : '查询' }}</el-button>
    </div>
    <EmptyState v-if="!access" title="暂无日志聚合数据" />
    <div v-else class="metric-grid">
      <el-card><span>请求数</span><strong>{{ metric('totalRequests', 'requestCount') }}</strong></el-card>
      <el-card><span>流量</span><strong>{{ formatBytes(metric('totalBytes', 'bodyBytesSent')) }}</strong></el-card>
      <el-card><span>缓存命中率</span><strong>{{ formatPercent(metric('cacheHitRate', 'cacheHitRatePercent')) }}</strong></el-card>
      <el-card><span>回源失败率</span><strong>{{ formatPercent(metric('originFailureRate', 'originFailureRatePercent')) }}</strong></el-card>
    </div>
    <el-alert
      v-if="access && !metric('totalRequests', 'requestCount')"
      title="访问日志采集可能尚未接入，当前仍会展示发布、操作和安全日志。"
      type="info"
      show-icon
    />

    <el-card>
      <template #header>访问聚合明细</template>
      <div class="log-stat-grid">
        <section>
          <h3>Top IP</h3>
          <EmptyState v-if="!topIps.length" title="暂无 Top IP 数据" />
          <ul v-else class="compact-list">
            <li v-for="item in topIps" :key="item.clientIp">
              <strong>{{ item.clientIp }}</strong>
              <span>请求 {{ formatNumber(item.requestCount) }}</span>
              <span>流量 {{ formatBytes(item.bodyBytesSent) }}</span>
              <span>{{ shareLabel(item.requestSharePercent) }}</span>
            </li>
          </ul>
        </section>
        <section>
          <h3>状态码分布</h3>
          <EmptyState v-if="!statusCodes.length" title="暂无状态码数据" />
          <ul v-else class="compact-list">
            <li v-for="item in statusCodes" :key="item.statusCode">
              <strong>HTTP {{ item.statusCode }}</strong>
              <span>{{ item.statusClass || '未分类' }}</span>
              <span>请求 {{ formatNumber(item.requestCount) }}</span>
              <span>{{ shareLabel(item.requestSharePercent) }}</span>
            </li>
          </ul>
        </section>
        <section>
          <h3>国家/地区</h3>
          <div v-if="!countries.length" class="empty-explain">
            <strong>暂无国家/地区数据</strong>
            <p>国家/地区统计依赖 Edge 访问日志中的国家字段，当前日志尚未携带或尚未聚合该字段。</p>
            <p>不影响访问日志、发布日志和操作日志展示。</p>
          </div>
          <ul v-else class="compact-list">
            <li v-for="item in countries" :key="item.countryCode">
              <strong>{{ countryLabel(item.countryCode) }}</strong>
              <span>请求 {{ formatNumber(item.requestCount) }}</span>
              <span>流量 {{ formatBytes(item.bodyBytesSent) }}</span>
              <span>{{ shareLabel(item.requestSharePercent) }}</span>
            </li>
          </ul>
        </section>
      </div>
    </el-card>

    <el-card>
      <template #header>发布日志</template>
      <EmptyState v-if="!publishLogs?.records?.length" title="暂无发布日志" />
      <ul v-else class="publish-list">
        <li v-for="row in publishLogs.records" :key="row.id">
          <strong>{{ releaseLabel(row) }}</strong>
          <span>{{ row.status || 'UNKNOWN' }}</span>
          <span>{{ row.action || 'PUBLISH' }}</span>
          <span>{{ nodeProgress(row) }}</span>
          <span>{{ logTimeText(row) }}</span>
          <small v-if="row.lastError">{{ row.lastError }}</small>
        </li>
      </ul>
    </el-card>

    <el-card>
      <template #header>操作日志</template>
      <EmptyState v-if="!operations?.records?.length" title="暂无操作日志" />
      <el-table v-else :data="operations.records">
        <el-table-column prop="eventType" label="事件类型" />
        <el-table-column prop="reason" label="说明" />
        <el-table-column prop="ipAddress" label="来源 IP" />
        <el-table-column label="发生时间">
          <template #default="{ row }">{{ logTimeText(row) }}</template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-card>
      <template #header>安全日志</template>
      <div v-if="!securityLogs.length" class="empty-explain">
        <strong>暂无安全日志</strong>
        <p>只有命中 IP 黑白名单、国家/地区策略或 WAF 规则后才会产生安全日志。</p>
        <p>当前为空表示没有安全事件命中，或安全事件采集链路尚未产生数据；不影响访问、发布和操作日志。</p>
      </div>
      <el-table v-else :data="securityLogs">
        <el-table-column prop="ruleName" label="规则" />
        <el-table-column prop="ruleType" label="类型" />
        <el-table-column prop="action" label="动作" />
        <el-table-column prop="riskLevel" label="风险等级" />
        <el-table-column prop="hitCount" label="命中数" />
        <el-table-column prop="blockedCount" label="拦截数" />
      </el-table>
    </el-card>
  </section>
</template>

<style scoped>
.log-stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 18px;
}

.compact-list,
.publish-list {
  display: grid;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.compact-list li,
.publish-list li {
  display: grid;
  gap: 8px;
  align-items: center;
  padding: 12px;
  border-radius: 12px;
  background: rgba(243, 248, 243, 0.92);
}

.compact-list li {
  grid-template-columns: 1.2fr repeat(3, minmax(70px, auto));
}

.publish-list li {
  grid-template-columns: 1fr repeat(4, minmax(84px, auto));
}

.publish-list small {
  grid-column: 1 / -1;
  color: #c0392b;
}

.empty-explain {
  display: grid;
  gap: 8px;
  border: 1px solid rgba(18, 63, 42, 0.1);
  border-radius: 14px;
  padding: 16px;
  color: #52675d;
  background: #f7fbf4;
}

.empty-explain strong {
  color: #173d29;
}

.empty-explain p {
  margin: 0;
  line-height: 1.6;
}
</style>
